import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, database } from '../../book/src/firebase';
import SpinnerOverlay from '../SpinnerOverlay';
import { ref, get, onValue, off, update } from 'firebase/database';
import ScheduleManager from 'src/book/src/data/ScheduleManager';
import { useAuth } from './AuthContext';
import Utils from 'src/book/src/data/Utils';
import { useLanguage } from 'src/LanguageContext';
import { DateTime } from 'luxon';
import { translate } from 'src/translate';
import Papa from 'papaparse';
import {
  getFilteredStaffKeys,
} from 'src/utils/staffAccessFilters';

const CustomersContext = createContext();

function aggregateByUid(events) {
  return events.reduce((acc, currentEvent) => {
    if (!currentEvent.uid) {
      return acc;
    }
    if (!acc[currentEvent.uid]) {
      acc[currentEvent.uid] = [];
    }
    acc[currentEvent.uid].push(currentEvent);

    return acc;
  }, {});
}

export const EventsProvider = ({ children }) => {
  const location = useLocation();
  const isAppointmentsRoute = location.pathname.startsWith('/manage/appointments');

  const [pastEvents, setPastEvents] = useState(null);
  const [currentEvents, setCurrentEvents] = useState(null);
  const [recurringEvents, setRecurringEvents] = useState(null);

  const [currentEventsByUser, setCurrentEventsByUser] = useState(null);
  const [pastEventsByUser, setPastEventsByUser] = useState(null);
  const [recurringEventsByUser, setRecurringEventsByUser] = useState(null);

  const [staffInfo, setStaffInfo] = useState(null);
  const [failedUsers, setFailedUsers] = useState({});

  const [customEvents, setCustomEvents] = React.useState([]);

  const { bizId, accessType, user, getCustomer, additionalConfigs } = useAuth();
  const { language } = useLanguage();
  const loading = false;

  const enrichEvents = (events) => {
    return events.map((event) => {
      const startFormatted = Utils.formatUnixTimestamp(event.startTimestamp, language);
      return {
        ...event,
        startFormatted: startFormatted,
      };
    });
  };

  React.useEffect(() => {
    const fetchFailedUsers = () => {
      const dbRef = ref(database, `/biz/failedUsers/${bizId}`);

      // Setting up the onValue listener
      onValue(
        dbRef,
        (snapshot) => {
          try {
            if (snapshot.exists()) {
              const info = snapshot.val();

              setFailedUsers(info);
              const xx = 'sd';

              // setStaffCalendarInfo(info);

              // setSelectedDays(info[selectedStaff]['workingHours']);
              // setScheduleData(info[selectedStaff]['specificOOOHours']);
            }
          } catch (error) {
            console.error(error);
          }
        },
        {
          onlyOnce: false,
        },
      );
    };

    if (bizId) {
      fetchFailedUsers();
    }

    // Cleanup function to remove the listener when the component unmounts or bizId changes
    return () => {
      const dbRef = ref(database, `/biz/failedUsers/${bizId}`);
      off(dbRef, 'value');
    };
  }, [bizId, accessType]);

  React.useEffect(() => {
    const fetchStaffInfo = () => {
      const dbRef = ref(database, `/biz/configs/${bizId}/staff`);

      // Setting up the onValue listener
      onValue(
        dbRef,
        (snapshot) => {
          try {
            if (snapshot.exists()) {
              const info = snapshot.val();
              const daysOfWeek = [
                'Sunday',
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
              ];

              for (const day of daysOfWeek) {
                for (const staff of Object.keys(info)) {
                  if (!info[staff]['workingHours']) {
                    info[staff]['workingHours'] = {};
                  }
                  if (!info[staff]['workingHours'][day]) {
                    info[staff]['workingHours'][day] = [];
                  }
                }
              }

              const yestrday = new Date();
              yestrday.setUTCHours(0, 0, 0, 0);
              yestrday.setDate(yestrday.getDate() - 1);

              for (const staff of Object.keys(info)) {
                if (!info[staff]['specificOOOHours']) {
                  info[staff]['specificOOOHours'] = {};
                } else {
                  info[staff]['specificOOOHours'] = Object.keys(
                    info[staff]['specificOOOHours'],
                  ).reduce((acc, timestamp) => {
                    const utcDate = DateTime.fromMillis(parseInt(timestamp), { zone: 'utc' });

                    const date = utcDate.setZone('Asia/Jerusalem');

                    const formattedDate = date.toFormat('dd/MM/yyyy');

                    if (info[staff]['specificOOOHours'][timestamp].closed) {
                      acc[formattedDate] = [];
                      return acc;
                    }

                    if (date <= yestrday || !info[staff]['specificOOOHours'][timestamp]) {
                      return acc;
                    }

                    acc[formattedDate] = info[staff]['specificOOOHours'][timestamp].map((slot) => ({
                      start: slot.start,
                      end: slot.end,
                    }));

                    return acc;
                  }, {});
                }

                // enrich apt options ids

                info[staff].aptOptionsIds = info[staff]?.aptOptions
                  ? Object.keys(info[staff]?.aptOptions)
                  : [];
              }

              const selectedStaff =
                Object.entries(info).find(([key, value]) => value.uid === user.uid) === undefined
                  ? Object.entries(info).filter((staff) => staff[1].isHidden !== true)[0][0]
                  : Object.entries(info).find(([key, value]) => value.uid === user.uid)[0];

              info['selectedStaff'] = selectedStaff;

              setStaffInfo(info);

              // setStaffCalendarInfo(info);

              // setSelectedDays(info[selectedStaff]['workingHours']);
              // setScheduleData(info[selectedStaff]['specificOOOHours']);
            }
          } catch (error) {
            console.error(error);
          }
        },
        {
          onlyOnce: false,
        },
      );
    };

    if (bizId) {
      fetchStaffInfo();
    }

    // Cleanup function to remove the listener when the component unmounts or bizId changes
    return () => {
      const dbRef = ref(database, `/biz/configs/${bizId}/staff`);
      off(dbRef, 'value');
    };
  }, [bizId, accessType]);

  useEffect(() => {
    if (!bizId) {
      return;
    }

    if (isAppointmentsRoute) {
      setCurrentEvents([]);
      setPastEvents([]);
      setCurrentEventsByUser({});
      setPastEventsByUser({});
    }

    const recurringEventsRef = ref(database, `/events/recurring/${bizId}`);
    const unsubscribers = [];

    if (!isAppointmentsRoute) {
      const currentEventsRef = ref(database, `/events/biz/${bizId}`);
      const pastEventsRef = ref(database, `/events/past/biz/${bizId}`);

      unsubscribers.push(
        onValue(
          currentEventsRef,
          (snapshot) => {
            if (snapshot.exists()) {
              const currentEventsData = ScheduleManager.flattenEvents(
                snapshot.val(),
                'upcomingEvent',
              );
              const enrichedCurrrentEvents = enrichEvents(currentEventsData);
              const currenEventsAggregatedEnriched = aggregateByUid(enrichedCurrrentEvents);

              setCurrentEvents(currentEventsData);
              setCurrentEventsByUser(currenEventsAggregatedEnriched);
            } else {
              setCurrentEvents([]);
              setCurrentEventsByUser({});
            }
          },
          { onlyOnce: false },
        ),
      );

      unsubscribers.push(
        onValue(
          pastEventsRef,
          (snapshot) => {
            if (snapshot.exists()) {
              const pastEventsData = ScheduleManager.flattenEvents(snapshot.val(), 'pastEvent');
              const enrichedPastEvents = enrichEvents(pastEventsData);
              const pastEventsAggregated = aggregateByUid(enrichedPastEvents);

              setPastEvents(pastEventsData);
              setPastEventsByUser(pastEventsAggregated);
            } else {
              setPastEvents([]);
              setPastEventsByUser({});
            }
          },
          { onlyOnce: false },
        ),
      );
    }

    unsubscribers.push(
      onValue(
        recurringEventsRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const recurringEventsData = snapshot.val();
            const recurringEventsFlat = [];
            const recurringEventsByUserData = {};

            Object.keys(recurringEventsData).forEach((uid) => {
              const userRecurringEvents = recurringEventsData[uid];
              recurringEventsByUserData[uid] = [];

              Object.keys(userRecurringEvents).forEach((firebaseKey) => {
                const recurringEvent = {
                  ...userRecurringEvents[firebaseKey],
                  firebaseKey: firebaseKey,
                };
                recurringEventsFlat.push(recurringEvent);
                recurringEventsByUserData[uid].push(recurringEvent);
              });
            });

            setRecurringEvents(recurringEventsFlat);
            setRecurringEventsByUser(recurringEventsByUserData);
          } else {
            setRecurringEvents([]);
            setRecurringEventsByUser({});
          }
        },
        { onlyOnce: false },
      ),
    );

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [bizId, database, isAppointmentsRoute]);

  useEffect(() => {
    if (!bizId) {
      setCustomEvents([]);
      return;
    }

    if (isAppointmentsRoute) {
      setCustomEvents([]);
      return;
    }

    const customEventsRef = ref(database, `/events/custom/${bizId}`);

    const onValueChange = onValue(
      customEventsRef,
      (snapshot) => {
        const customEventsEntries = [];

        if (snapshot.exists()) {
          const customEvents = snapshot.val();
          const customEventsBreaks = customEvents.breaks;

          for (const key in customEventsBreaks) {
            // Extract each inner object and add it to the array
            for (const innerKey in customEventsBreaks[key]) {
              customEventsEntries.push(customEventsBreaks[key][innerKey]);
            }
          }

          // Split multi-day break events into individual single-day events
          const splitEvents = ScheduleManager.splitMultiDayBreakEvents(customEventsEntries);
          setCustomEvents(splitEvents);
        } else {
          setCustomEvents([]);
        }
      },
      (error) => {
        console.error('Firebase read failed: ', error);
        setCustomEvents([]);
      },
    );

    // Return function to clean up the subscription
    return () => {
      off(customEventsRef, 'value', onValueChange);
    };
  }, [bizId, database, isAppointmentsRoute]);

  const getAptStaffMembers = (aptId) => {
    const aptStaffMembers = [];

    if (!staffInfo || !aptId) return [];

    for (const staffKey in staffInfo) {
      if (staffKey === 'selectedStaff') continue;

      const staff = staffInfo[staffKey];
      if (staff.aptOptionsIds && staff.aptOptionsIds.includes(aptId)) {
        // this is how we remove staff:
        if (
          staff?.aptOptions[aptId]?.isHidden === true &&
          staff?.aptOptions[aptId]?.overrideHiddenInDashboard !== true
        ) {
          continue;
        }

        aptStaffMembers.push({
          id: staffKey,
          title: staff.title,
          color: staff.color ? staff.color : 'blue',
        });
      }
    }

    return aptStaffMembers;
  };

  const getAllStaffMembers = () => {
    const defaultColors = ['blue', 'yellow', 'green', 'red', 'indigo'];

    if (!staffInfo) return [];

    // First, create array and sort by ID
    const aptStaffMembers = Object.entries(staffInfo)
      .filter(([key]) => key !== 'selectedStaff')
      .map(([staffKey, staff]) => ({
        id: staffKey,
        title: staff.title,
        color: staff.color || '', // Temporarily set empty if no color
        description: staff.description,
        isHidden: staff.isHidden === true ? true : false,
        uid: staff.uid,
      }))
      .sort((a, b) => a.id.localeCompare(b.id));

    // Get already used colors
    const usedColors = new Set(
      aptStaffMembers.map((staff) => staff.color).filter((color) => color !== ''),
    );

    // Create array of available colors
    const availableColors = defaultColors.filter((color) => !usedColors.has(color));
    let colorIndex = 0;

    // Assign colors to staff members without colors
    return aptStaffMembers.map((staff) => {
      if (!staff.color && availableColors.length > 0) {
        staff.color = availableColors[colorIndex];
        colorIndex = (colorIndex + 1) % availableColors.length;
      }
      return staff;
    });
  };

  const getAllStaffMembersOptions = () => {
    return getFilteredStaffKeys(staffInfo, {
      additionalConfigs,
      accessType,
      userUid: user?.uid,
    }).map((staffKey) => ({
      value: staffKey,
      label: Utils.getLanguageOrDefault(staffInfo[staffKey], language),
    }));
  };

  const getAllStaffMembersIds = () => {
    return getFilteredStaffKeys(staffInfo, {
      additionalConfigs,
      accessType,
      userUid: user?.uid,
    });
  };

  const hasAccessibleStaffMembers = () => {
    if (!staffInfo || accessType === null) return null;
    if (accessType === 'STAFF' && additionalConfigs === null) return null;

    return (
      getFilteredStaffKeys(staffInfo, {
        additionalConfigs,
        accessType,
        userUid: user?.uid,
      }).length > 0
    );
  };

  const getStaffInfo = (staffId) => {
    const allStaff = getAllStaffMembers();
    const staff = allStaff.filter((stf) => stf.id === staffId);

    if (staff.length === 1) {
      return staff[0];
    }
    return null;
  };

  const updateEventCapWeight = async (eventId, uid, newCapWeight, isPast = false) => {
    const eventRef = ref(database, `/events/biz/${bizId}/${uid}/${eventId}/aptBooking`);
    const pastEventRef = ref(database, `/events/past/biz/${bizId}/${uid}/${eventId}/aptBooking`);
    try {
      if (isPast) {
        await update(pastEventRef, {
          capWeight: newCapWeight,
        });
      } else {
        await update(eventRef, {
          capWeight: newCapWeight,
        });
      }
      // console.log('[GalilRiders] capWeight updated to', newCapWeight, 'for event', eventId);
    } catch (error) {
      console.error('[GalilRiders] Failed to update capWeight:', error);
    }
  };

  const addEventNote = async (eventId, uid, note) => {
    const eventRef = ref(database, `/events/biz/${bizId}/${uid}/${eventId}`);
    const pastEventRef = ref(database, `/events/past/biz/${bizId}/${uid}/${eventId}`);
    try {
      if (currentEvents.find((event) => event.eventId === eventId)) {
        await update(eventRef, {
          eventNotes: note,
        });
      } else {
        await update(pastEventRef, {
          eventNotes: note,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const exportDayToCsv = (events) => {
    if (events === null) return null;
    try {
      const filteredEvents = events.filter((event) => event.staffId);
      const processedData = filteredEvents.map((event) => {
        // Fill in each question, using a dash "-" where data is missing

        // const filledQuestions = allQuestionKeys.reduce((acc, key) => {
        //   const headerKey = Utils.getLanguageOrDefault({ strings: questions[key] }, language);

        //   acc[headerKey] = item.questions[key] ? item.questions[key].val : '-';
        //   return acc;
        // }, {});

        return {
          // ...filledQuestions,

          [translate('phoneNumber', language)]: `="+${getCustomer(event.uid)?.number || ''}"`,
          [translate('name', language)]: getCustomer(event.uid)?.name,
          [translate('appoitment', language)]: Utils.getEventTitle(event, language),
          [translate('date', language)]: event.dateFormatted,
          [translate('start', language)]: event.startFormatted,
          [translate('end', language)]: event.endFormatted,
        };
      });

      const csv = Papa.unparse(processedData, {
        header: true,
        delimiter: ',', // You can specify a custom delimiter
      });

      const BOM = '\uFEFF';
      const csvBlob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
      const csvUrl = URL.createObjectURL(csvBlob);

      return csvUrl;
    } catch (err) {
      return null;
    }
  };

  // const [pastEvents, setPastEvents] = useState(null);
  // const [currentEvents, setCurrentEvents] = useState(null);

  // const [currentEventsByUser, setCurrentEventsByUser] = useState(null);
  // const [pastEventsByUser, setPastEventsByUser] = useState(null);

  // const [staffInfo, setStaffInfo] = useState(null);

  return (
    <CustomersContext.Provider
      value={{
        pastEvents,
        currentEvents,
        recurringEvents,
        currentEventsByUser,
        pastEventsByUser,
        recurringEventsByUser,
        staffInfo,
        failedUsers,
        getAptStaffMembers,
        getAllStaffMembers,
        getAllStaffMembersIds,
        getStaffInfo,
        getAllStaffMembersOptions,
        hasAccessibleStaffMembers,
        customEvents,
        addEventNote,
        updateEventCapWeight,
        exportDayToCsv,
      }}
    >
      {!loading ? children : <SpinnerOverlay />}
    </CustomersContext.Provider>
  );
};

export const useEvents = () => useContext(CustomersContext);
